/**
 * Stable-tile detection for template swap crossfade.
 *
 * Per core-flows §2.2: tiles present in BOTH the old and new board — matched by
 * `type` — are "stable" and excluded from the crossfade animation. Only tiles that
 * appear in one board but not the other are animated in/out.
 *
 * Matching is by tile TYPE (not id, since ids differ across templates).
 * If the old board has two tiles of the same type and the new board has one, the
 * first match is considered stable and the second is animated out.
 *
 * This module is pure and does not depend on React or the DOM.
 */
import type { Board } from 'briefpane/schema';

export interface CrossfadeClassification {
  /** Tile ids in the NEW board that are stable (type existed in the old board). */
  readonly stableIncoming: ReadonlySet<string>;
  /** Tile ids in the OLD board that are stable (type exists in the new board). */
  readonly stableOutgoing: ReadonlySet<string>;
  /** Tile ids in the NEW board that are entering (animated in). */
  readonly entering: ReadonlySet<string>;
  /** Tile ids in the OLD board that are leaving (animated out). */
  readonly leaving: ReadonlySet<string>;
}

/**
 * Classify tiles across a template swap: which are stable vs animated.
 *
 * @param oldBoard The board being replaced.
 * @param newBoard The board being swapped in.
 * @returns Classification of each tile's animation role.
 */
export function classifyTilesForCrossfade(
  oldBoard: Board,
  newBoard: Board,
): CrossfadeClassification {
  // Build a mutable frequency map of types in the old board.
  const oldTypePool = new Map<string, string[]>();
  for (const tile of oldBoard.tiles) {
    const existing = oldTypePool.get(tile.type) ?? [];
    oldTypePool.set(tile.type, [...existing, tile.id]);
  }

  const stableIncoming = new Set<string>();
  const stableOutgoing = new Set<string>();
  const entering = new Set<string>();

  // Walk the new board's tiles, matching against the old board by type.
  for (const tile of newBoard.tiles) {
    const pool = oldTypePool.get(tile.type);
    if (pool && pool.length > 0) {
      // A match exists in the old board — both sides are stable.
      const matchedOldId = pool[0];
      // Remove the match so it can only be used once (handles duplicates).
      oldTypePool.set(tile.type, pool.slice(1));
      stableIncoming.add(tile.id);
      if (matchedOldId !== undefined) {
        stableOutgoing.add(matchedOldId);
      }
    } else {
      entering.add(tile.id);
    }
  }

  // Any remaining old-board tiles not matched are leaving.
  const leaving = new Set<string>();
  for (const ids of oldTypePool.values()) {
    for (const id of ids) {
      leaving.add(id);
    }
  }

  return { stableIncoming, stableOutgoing, entering, leaving };
}
